import { Form, Input, InputProps, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { FormLabel } from './FormLabel';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface Props extends InputProps {
  fieldName: string;
  label: string;
  showWaybillNote?: boolean;
}

export const PhoneInput = ({ fieldName, label, showWaybillNote = false, ...rest }: Props): JSX.Element => {
  const { getFieldMeta, handleChange, getFieldHelpers } = useFormikContext<any>();
  const { value, touched, error } = getFieldMeta<string>(fieldName);
  const { setTouched } = getFieldHelpers(fieldName);

  // Format phone number for display (add dashes)
  const formatPhoneNumber = (value: string) => {
    if (!value) return '';

    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length <= 3) return digitsOnly;

    if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3)}`;

    return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  };

  // Handle input change with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digitsOnly = input.replace(/\D/g, '');

    // Limit to 10 digits
    if (digitsOnly.length <= 10) {
      handleChange(fieldName)(e);
    }
  };

  // Get current digit count for validation feedback
  const currentDigitCount = value ? value.replace(/\D/g, '').length : 0;
  const isValidLength = currentDigitCount === 10;
  const hasValue = value && value.trim().length > 0;

  return (
    <Form.Item
      hasFeedback={touched && !error}
      labelAlign="left"
      validateStatus={touched && error ? 'error' : hasValue && isValidLength ? 'success' : ''}
      label={<FormLabel label={label} />}
      help={touched && error ? error : hasValue && !isValidLength ? <Text type="warning">Phone number must be exactly 10 digits (currently {currentDigitCount})</Text> : undefined}
      style={{ marginBottom: 0, color: 'rgba(0,0,0,0.85)' }}>
      <Input
        {...rest}
        value={formatPhoneNumber(value || '')}
        onChange={handlePhoneChange}
        onFocus={() => setTouched(false)}
        onBlur={() => setTouched(true)}
        placeholder="123-456-7890"
        maxLength={12} // 3-3-4 format
      />

      {/* Waybill generation note */}
      {showWaybillNote && (
        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
          <InfoCircleOutlined style={{ marginRight: 4 }} />
          <Text type="secondary">Note: A valid 10-digit phone number is required to generate waybills for defective returns.</Text>
        </div>
      )}

      {/* Character count indicator */}
      {hasValue && <div style={{ marginTop: 4, fontSize: '11px', color: isValidLength ? '#52c41a' : '#faad14' }}>{currentDigitCount}/10 digits</div>}
    </Form.Item>
  );
};
